import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { CheckCircle, ArrowRight, Crown, AlertCircle, Loader } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BillingSuccess = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (sessionId) {
      checkPaymentStatus();
      // Poll for status updates every 2 seconds for up to 30 seconds
      const interval = setInterval(checkPaymentStatus, 2000);
      setTimeout(() => clearInterval(interval), 30000);
      
      return () => clearInterval(interval);
    } else {
      setError('No session ID found');
      setLoading(false);
    }
  }, [sessionId]);

  const checkPaymentStatus = async () => {
    try {
      const response = await axios.get(`${API}/billing/checkout/status/${sessionId}`, { withCredentials: true });
      setPaymentStatus(response.data);
      
      // Stop polling if payment is complete (success or failure)
      if (response.data.payment_status === 'paid' || response.data.payment_status === 'failed') {
        setLoading(false);
      }
    } catch (error) {
      console.error('Failed to check payment status:', error);
      setError('Failed to verify payment status');
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-bissa-light">
        <Card className="w-full max-w-md">
          <CardContent className="pt-12 pb-12">
            <div className="text-center space-y-6">
              <div className="w-16 h-16 bg-bissa-blue-50 rounded-full flex items-center justify-center mx-auto">
                <Loader className="w-8 h-8 text-bissa-blue animate-spin" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-bissa-blue mb-2">Processing Your Payment</h2>
                <p className="text-gray-600">
                  Please wait while we confirm your payment and activate your subscription...
                </p>
              </div>
              <div className="text-sm text-gray-500">
                This may take a few moments
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error || !paymentStatus) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-bissa-light">
        <Card className="w-full max-w-md">
          <CardContent className="pt-12 pb-12">
            <div className="text-center space-y-6">
              <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto">
                <AlertCircle className="w-8 h-8 text-red-600" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Payment Verification Failed</h2>
                <p className="text-gray-600 mb-6">
                  {error || 'We could not verify your payment. Please contact support if you believe this is an error.'}
                </p>
                <div className="space-y-3">
                  <Button asChild className="w-full">
                    <Link to="/billing">Back to Billing</Link>
                  </Button>
                  <Button variant="outline" asChild className="w-full">
                    <Link to="/settings">Contact Support</Link>
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const isSuccessful = paymentStatus.payment_status === 'paid';

  return (
    <div className="min-h-screen flex items-center justify-center bg-bissa-light">
      <Card className="w-full max-w-md">
        <CardContent className="pt-12 pb-12">
          <div className="text-center space-y-6">
            {/* Success/Failure Icon */}
            <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto ${
              isSuccessful ? 'bg-green-50' : 'bg-red-50'
            }`}>
              {isSuccessful ? (
                <CheckCircle className="w-8 h-8 text-green-600" />
              ) : (
                <AlertCircle className="w-8 h-8 text-red-600" />
              )}
            </div>

            {/* Title and Message */}
            <div>
              <h2 className={`text-2xl font-bold mb-2 ${
                isSuccessful ? 'text-bissa-blue' : 'text-gray-900'
              }`}>
                {isSuccessful ? 'Payment Successful!' : 'Payment Failed'}
              </h2>
              <p className="text-gray-600">
                {isSuccessful 
                  ? 'Your subscription has been activated. Welcome to your new plan!' 
                  : 'Your payment could not be processed. Please try again.'
                }
              </p>
            </div>

            {/* Payment Details */}
            {isSuccessful && (
              <div className="bg-bissa-light p-4 rounded-lg space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Amount Paid:</span>
                  <span className="font-semibold text-bissa-blue">
                    ${paymentStatus.amount} {paymentStatus.currency.toUpperCase()}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Plan:</span>
                  <span className="font-semibold text-bissa-blue capitalize">
                    {paymentStatus.plan_id} Plan
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Status:</span>
                  <div className="flex items-center gap-2">
                    <Crown className="w-4 h-4 text-bissa-gold" />
                    <span className="font-semibold text-bissa-gold">Active</span>
                  </div>
                </div>
              </div>
            )}

            {/* Next Steps */}
            <div>
              <h3 className="font-semibold text-bissa-blue mb-3">What's Next?</h3>
              <div className="space-y-3 text-sm text-left">
                {isSuccessful ? (
                  <>
                    <div className="flex items-start gap-3">
                      <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                      <span>Your new plan features are now active</span>
                    </div>
                    <div className="flex items-start gap-3">
                      <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                      <span>Increased limits are immediately available</span>
                    </div>
                    <div className="flex items-start gap-3">
                      <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                      <span>Access new premium features in your dashboard</span>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="flex items-start gap-3">
                      <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                      <span>Try using a different payment method</span>
                    </div>
                    <div className="flex items-start gap-3">
                      <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                      <span>Check with your bank about the transaction</span>
                    </div>
                    <div className="flex items-start gap-3">
                      <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                      <span>Contact our support team for assistance</span>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="space-y-3">
              <Button asChild className="w-full bg-bissa-blue text-white hover:bg-bissa-gold hover:text-bissa-blue">
                <Link to="/dashboard">
                  Go to Dashboard
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Link>
              </Button>
              
              <div className="flex gap-3">
                <Button variant="outline" asChild className="flex-1">
                  <Link to="/billing">View Billing</Link>
                </Button>
                {!isSuccessful && (
                  <Button variant="outline" asChild className="flex-1">
                    <Link to="/settings">Get Help</Link>
                  </Button>
                )}
              </div>
            </div>

            {/* Contact Support */}
            <div className="pt-4 border-t text-xs text-gray-500">
              Need help? Contact us at{' '}
              <a href="mailto:support@bissa.ai" className="text-bissa-blue hover:underline">
                support@bissa.ai
              </a>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default BillingSuccess;